import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  ChevronRight, 
  ChevronDown,
  Maximize2,
  Minimize2,
  Save,
  X,
  FolderOpen,
  Folder,
  AlertCircle,
  GripVertical,
  Loader,
  FileText,
  List,
  Globe,
  Filter
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  collection,
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useClient } from '../../contexts/ClientContext';
import AttributesModal, { CategoryAttribute } from './AttributesModal';
import GeneralRulesModal from './GeneralRulesModal';
import ClassifierRulesModal from './ClassifierRulesModal';
import GlobalAttributesModal from './GlobalAttributesModal';
import FilteredProductsModal from '../FilteredProductsModal';
import api from '../../utils/api';

interface CategoryNode {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  children: CategoryNode[];
  productCount?: number;
  order?: number;
  attributes?: CategoryAttribute[];
  rules?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

interface FlatNode extends CategoryNode {
  path: string[];
}

interface DropIndicator {
  targetId: string;
  position: 'before' | 'after' | 'inside';
  level: number;
}

// Componente para cada nodo del árbol
const TreeNode: React.FC<{
  node: CategoryNode;
  expanded: boolean;
  onToggle: () => void;
  onEdit: (node: CategoryNode) => void;
  onDelete: (node: CategoryNode) => void;
  onAddChild: (node: CategoryNode) => void;
  onOpenAttributes: (node: CategoryNode) => void;
  searchTerm: string;
  isHighlighted: boolean;
  isDragging?: boolean;
  dropIndicator?: DropIndicator | null;
  productCounts: { [key: string]: number };
  setSelectedCategoryForProducts: (name: string) => void;
  setShowProductsModal: (show: boolean) => void;
}> = ({ 
  node, 
  expanded, 
  onToggle, 
  onEdit, 
  onDelete, 
  onAddChild,
  onOpenAttributes,
  searchTerm, 
  isHighlighted,
  isDragging = false,
  dropIndicator,
  productCounts,
  setSelectedCategoryForProducts,
  setShowProductsModal = null
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Función para resaltar el texto de búsqueda
  const highlightText = (text: string) => {
    if (!searchTerm || !isHighlighted) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === searchTerm.toLowerCase() ? 
            <span key={i} className="bg-yellow-300 font-semibold">{part}</span> : 
            part
        )}
      </span>
    );
  };

  const hasChildren = node.children && node.children.length > 0;
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`select-none ${isDragging ? 'opacity-50' : ''} relative`}
    >
      {/* Indicador de drop - línea superior */}
      {dropIndicator && dropIndicator.targetId === node.id && dropIndicator.position === 'before' && (
        <div 
          className={`absolute -top-px left-0 right-0 h-0.5 bg-blue-500 z-50`}
          style={{ 
            marginLeft: `${dropIndicator.level * 1.5}rem`,
            backgroundImage: 'repeating-linear-gradient(90deg, #3b82f6 0, #3b82f6 4px, transparent 4px, transparent 8px)'
          }}
        >
          <div className="absolute -left-1.5 -top-1 w-2 h-2 bg-blue-500 rounded-full" />
        </div>
      )}
      
      <div 
        className={`
          flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group
          ${isHighlighted ? 'bg-yellow-50' : ''}
          ${node.level === 0 ? 'font-semibold' : ''}
          ${node.level === 1 ? 'ml-6' : ''}
          ${node.level === 2 ? 'ml-12' : ''}
          ${dropIndicator && dropIndicator.targetId === node.id && dropIndicator.position === 'inside' ? 'ring-4 ring-blue-500 bg-blue-100 shadow-lg' : ''}
        `}
      >
        <div className="flex items-center flex-1">
          <button 
            onClick={onToggle}
            className="mr-2 p-1 hover:bg-gray-200 rounded"
          >
            {hasChildren ? (
              expanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>
          
          <div 
            {...attributes}
            {...listeners}
            className="cursor-move mr-2 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>

          {expanded || !hasChildren ? (
            <FolderOpen className="w-4 h-4 text-blue-500 mr-2" />
          ) : (
            <Folder className="w-4 h-4 text-gray-500 mr-2" />
          )}
          
          <span className="flex-1 flex items-center gap-2">
            {highlightText(node.name)}
            {(() => {
              const count = productCounts && productCounts[node.name];
              if (!count && productCounts && Object.keys(productCounts).length > 0) {
              }
              return count && count > 0 ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategoryForProducts?.(node.name);
                    setShowProductsModal?.(true);
                  }}
                  className="inline-flex items-center px-2 py-0.5 text-xs rounded-full transition-colors hover:opacity-80 cursor-pointer" 
                  style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: 'rgb(59, 130, 246)',
                    border: '1px solid rgba(59, 130, 246, 0.3)'
                  }}
                  title={`${count} productos en la toma actual - Click para ver`}>
                  {count}
                </button>
              ) : null;
            })()}
            {node.attributes && node.attributes.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAttributes(node);
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-colors hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(219, 39, 119, 0.1)',
                  color: 'rgb(219, 39, 119)',
                  border: '1px solid rgba(219, 39, 119, 0.3)'
                }}
                title={`${node.attributes.length} atributo${node.attributes.length > 1 ? 's' : ''} - Click para editar`}
              >
                <List className="w-3 h-3" />
                {node.attributes.length}
              </button>
            )}
          </span>
        </div>
        
        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {node.level < 2 && (
            <button
              onClick={() => onAddChild(node)}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Agregar subcategoría"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onOpenAttributes(node)}
            className="p-1 text-pink-600 hover:bg-pink-50 rounded"
            title="Gestionar atributos"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(node)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="Editar nombre"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Indicador de drop - línea inferior */}
      {dropIndicator && dropIndicator.targetId === node.id && dropIndicator.position === 'after' && (
        <div 
          className={`absolute -bottom-px left-0 right-0 h-0.5 bg-blue-500 z-50`}
          style={{ 
            marginLeft: `${dropIndicator.level * 1.5}rem`,
            backgroundImage: 'repeating-linear-gradient(90deg, #3b82f6 0, #3b82f6 4px, transparent 4px, transparent 8px)'
          }}
        >
          <div className="absolute -left-1.5 -top-1 w-2 h-2 bg-blue-500 rounded-full" />
        </div>
      )}
    </div>
  );
};

interface CategoriasProps {
  /**
   * Si se pasa, se usa este clientId en lugar del seleccionado en context.
   * Útil para mostrar el editor desde un panel admin donde el context está
   * en modo "admin-mode".
   */
  clientIdOverride?: string;
  clientNameOverride?: string;
}

const Categorias: React.FC<CategoriasProps> = ({ clientIdOverride, clientNameOverride }) => {
  const { selectedClient: selectedClientFromCtx } = useClient();
  // IMPORTANTE: memoizar para que el objeto sea estable y no dispare re-suscripciones
  // infinitas en los useEffect que dependen de `selectedClient`.
  const selectedClient = useMemo(() => {
    if (clientIdOverride) {
      return { clientId: clientIdOverride, clientName: clientNameOverride || clientIdOverride };
    }
    return selectedClientFromCtx;
  }, [clientIdOverride, clientNameOverride, selectedClientFromCtx]);
  console.log('[Categorias] mount/render', {
    clientIdOverride,
    selectedClientId: selectedClient?.clientId,
    fromCtx: selectedClientFromCtx?.clientId,
  });
  
  // Estado del árbol de categorías
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [newNodeName, setNewNodeName] = useState('');
  const [showNewRootForm, setShowNewRootForm] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [_overId, setOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Estados para los modales
  const [selectedNodeForAttributes, setSelectedNodeForAttributes] = useState<CategoryNode | null>(null);
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showClassifierModal, setShowClassifierModal] = useState(false);
  const [showGlobalAttributesModal, setShowGlobalAttributesModal] = useState(false);
  const [rulesCount, setRulesCount] = useState(0);
  const [globalAttributesCount, setGlobalAttributesCount] = useState(0);
  const [productCounts, setProductCounts] = useState<{ [key: string]: number }>({});
  const [currentTableName, setCurrentTableName] = useState<string | null>(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedCategoryForProducts, setSelectedCategoryForProducts] = useState<string>('');

  // Obtener referencia a la colección de categorías del cliente
  const getCategoriesCollection = useCallback(() => {
    if (!selectedClient?.clientId || selectedClient.clientId === 'admin-mode') {
      return null;
    }
    return collection(db, 'clients', selectedClient.clientId, 'categories');
    // Dep por clientId (string estable), no por el objeto selectedClient, para
    // no recrear la collection ref en cada render y re-suscribir onSnapshot en loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.clientId]);

  // Función para obtener el conteo de productos
  const fetchProductCounts = useCallback(async () => {
    if (!selectedClient || !currentTableName) return;
    
    try {
      const response = await api.get(
        `/api/tables/${currentTableName}/catalogado-counts`,
        {
          headers: {
            'X-Client-Id': selectedClient.clientId
          }
        }
      );
      
      if (response.data && response.data.categories) {
        setProductCounts(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching product counts:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.clientId, currentTableName]);

  // Obtener nombre de la tabla actual
  useEffect(() => {
    const fetchCurrentTable = async () => {
      if (!selectedClient) return;
      
      try {
        const response = await api.get(
          `/api/tables`,
          {
            headers: {
              'X-Client-Id': selectedClient.clientId
            }
          }
        );
        
        if (response.data && response.data.length > 0) {
          // La respuesta tiene formato: [{ "Tables_in_xxx": "toma_14" }]
          const tables = response.data.map((item: any) => {
            const key = Object.keys(item)[0];
            return item[key];
          });
          
          // Tomar la tabla más reciente
          const sortedTables = tables.sort((a: string, b: string) => {
            const numA = parseInt(a.replace('toma_', ''));
            const numB = parseInt(b.replace('toma_', ''));
            return numB - numA;
          });
          
          const tableName = sortedTables[0];
          setCurrentTableName(tableName);
        }
      } catch (error) {
        console.error('Error fetching tables:', error);
      }
    };
    
    fetchCurrentTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.clientId]);

  // Obtener conteos cuando cambie la tabla
  useEffect(() => {
    if (currentTableName) {
      fetchProductCounts();
    }
  }, [currentTableName, fetchProductCounts]);

  // Cargar categorías desde Firestore
  useEffect(() => {
    const categoriesCollection = getCategoriesCollection();
    console.log('[Categorias] effect: load categories', {
      clientId: selectedClient?.clientId,
      hasCollection: !!categoriesCollection,
    });
    if (!categoriesCollection) {
      setLoading(false);
      setCategories([]);
      return;
    }

    setLoading(true);

    // Suscribirse a cambios en tiempo real
    const unsubscribe = onSnapshot(
      categoriesCollection,
      (snapshot) => {
        console.log('[Categorias] snapshot recibido', {
          clientId: selectedClient?.clientId,
          docs: snapshot.docs.length,
        });
        const categoriesData: CategoryNode[] = [];
        const categoriesMap = new Map<string, CategoryNode>();
        
        // Primero, crear todos los nodos
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const category: CategoryNode = {
            id: doc.id,
            name: data.name,
            parentId: data.parentId || null,
            level: data.level || 0,
            order: data.order || 0,
            productCount: data.productCount || 0,
            attributes: data.attributes || [],
            rules: data.rules || '',
            children: [],
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          };
          categoriesMap.set(category.id, category);
        });
        
        // Luego, construir el árbol
        categoriesMap.forEach(category => {
          if (category.parentId) {
            const parent = categoriesMap.get(category.parentId);
            if (parent) {
              parent.children.push(category);
            }
          } else {
            categoriesData.push(category);
          }
        });
        
        // Ordenar alfabéticamente por nombre en todos los niveles
        const sortNodes = (nodes: CategoryNode[]) => {
          nodes.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
          nodes.forEach(node => {
            if (node.children.length > 0) {
              sortNodes(node.children);
            }
          });
        };
        
        sortNodes(categoriesData);
        setCategories(categoriesData);
        setLoading(false);
      },
      (error) => {
        console.error('[Categorias] Error loading categories:', error);
        setError('Error al cargar las categorías: ' + (error?.message || error));
        setLoading(false);
      }
    );

    return () => {
      console.log('[Categorias] unsubscribe categories', { clientId: selectedClient?.clientId });
      unsubscribe();
    };
    // Dep por clientId (via getCategoriesCollection ya memoizado por clientId).
    // NO depender del objeto selectedClient: cambia de referencia por render y
    // re-suscribe onSnapshot en loop → setCategories en bucle → freeze al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCategoriesCollection]);

  // Cargar conteo de reglas desde Firestore
  useEffect(() => {
    if (!selectedClient?.clientId || selectedClient.clientId === 'admin-mode') {
      setRulesCount(0);
      setGlobalAttributesCount(0);
      return;
    }

    const rulesDoc = doc(db, 'clients', selectedClient.clientId, 'catalogRules', 'general');
    const globalAttributesDoc = doc(db, 'clients', selectedClient.clientId, 'catalogRules', 'globalAttributes');
    
    const unsubscribeRules = onSnapshot(
      rulesDoc,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const rules = data.rules || [];
          setRulesCount(rules.length);
        } else {
          setRulesCount(0);
        }
      },
      (error) => {
        console.error('Error loading rules count:', error);
        setRulesCount(0);
      }
    );

    const unsubscribeGlobalAttrs = onSnapshot(
      globalAttributesDoc,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const attributes = data.attributes || [];
          setGlobalAttributesCount(attributes.length);
        } else {
          setGlobalAttributesCount(0);
        }
      },
      (error) => {
        console.error('Error loading global attributes count:', error);
        setGlobalAttributesCount(0);
      }
    );

    return () => {
      unsubscribeRules();
      unsubscribeGlobalAttrs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.clientId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Aplanar el árbol para la búsqueda y el drag & drop
  const flattenTree = useCallback((nodes: CategoryNode[], path: string[] = []): FlatNode[] => {
    return nodes.reduce((acc: FlatNode[], node) => {
      const currentPath = [...path, node.name];
      const flatNode: FlatNode = { ...node, path: currentPath };
      
      if (expandedNodes.has(node.id) && node.children) {
        return [...acc, flatNode, ...flattenTree(node.children, currentPath)];
      }
      
      return [...acc, flatNode];
    }, []);
  }, [expandedNodes]);

  // Buscar nodos que coinciden con el término de búsqueda
  const searchInTree = useCallback((nodes: CategoryNode[], term: string): Set<string> => {
    const matchingIds = new Set<string>();
    
    if (!term) return matchingIds;
    
    const searchRecursive = (nodeList: CategoryNode[], parentMatches: boolean = false) => {
      nodeList.forEach(node => {
        const matches = node.name.toLowerCase().includes(term.toLowerCase());
        
        if (matches || parentMatches) {
          matchingIds.add(node.id);
          // Marcar todos los padres para expandir
          let currentId = node.parentId;
          while (currentId) {
            matchingIds.add(currentId);
            // Buscar el padre en la lista plana
            const parent = findNodeInList(nodes, currentId);
            if (parent) {
              currentId = parent.parentId;
            } else {
              break;
            }
          }
        }
        
        if (node.children && node.children.length > 0) {
          searchRecursive(node.children, matches);
        }
      });
    };
    
    searchRecursive(nodes);

    // NOTA: función PURA. NO llamar setExpandedNodes acá (anti-patrón: setState
    // durante render). La auto-expansión de los nodos que matchean vive en un
    // useEffect que depende de `highlightedNodes` (ver abajo).
    return matchingIds;
  }, []);

  const highlightedNodes = useMemo(() => {
    return searchInTree(categories, searchTerm);
  }, [categories, searchTerm, searchInTree]);

  // Auto-expandir los nodos que matchean la búsqueda (y sus padres).
  // Se hace en un effect, NO dentro del useMemo, para evitar setState en render.
  useEffect(() => {
    if (highlightedNodes.size === 0) return;
    setExpandedNodes(prev => {
      let changed = false;
      const newSet = new Set(prev);
      highlightedNodes.forEach(id => {
        if (!newSet.has(id)) {
          newSet.add(id);
          changed = true;
        }
      });
      // Evitar re-render innecesario si no cambió nada.
      return changed ? newSet : prev;
    });
  }, [highlightedNodes]);

  // Verificar si un nombre ya existe en el árbol
  const isNameUnique = (name: string, excludeId?: string): boolean => {
    const checkRecursive = (nodes: CategoryNode[]): boolean => {
      for (const node of nodes) {
        if (node.id !== excludeId && node.name.toLowerCase() === name.toLowerCase()) {
          return false;
        }
        if (node.children && !checkRecursive(node.children)) {
          return false;
        }
      }
      return true;
    };
    
    return checkRecursive(categories);
  };

  // Encontrar un nodo por ID
  const findNodeById = (nodes: CategoryNode[], id: string): CategoryNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Encontrar un nodo por ID en una lista plana
  const findNodeInList = (nodes: CategoryNode[], id: string): CategoryNode | null => {
    const searchInNodes = (nodeList: CategoryNode[]): CategoryNode | null => {
      for (const node of nodeList) {
        if (node.id === id) return node;
        if (node.children) {
          const found = searchInNodes(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return searchInNodes(nodes);
  };

  // Expandir todos los nodos
  const expandAll = () => {
    const getAllIds = (nodes: CategoryNode[]): string[] => {
      return nodes.reduce((acc: string[], node) => {
        const childIds = node.children ? getAllIds(node.children) : [];
        return [...acc, node.id, ...childIds];
      }, []);
    };
    
    const allIds = getAllIds(categories);
    const newSet = new Set<string>();
    allIds.forEach(id => newSet.add(id));
    setExpandedNodes(newSet);
  };

  // Colapsar todos los nodos
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Obtener todas las categorías en formato plano con su jerarquía
  const getAllCategoriesFlat = (): { id: string; name: string; path: string }[] => {
    const result: { id: string; name: string; path: string }[] = [];
    
    const addNodeToFlat = (node: CategoryNode, parentPath: string = '') => {
      const currentPath = parentPath ? `${parentPath} > ${node.name}` : node.name;
      result.push({
        id: node.id,
        name: node.name,
        path: currentPath
      });
      
      if (node.children) {
        node.children.forEach(child => addNodeToFlat(child, currentPath));
      }
    };
    
    categories.forEach(node => addNodeToFlat(node));
    return result;
  };

  // Toggle expandir/colapsar nodo
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Agregar nueva categoría raíz en Firestore
  const addRootCategory = async () => {
    if (!newNodeName.trim()) {
      setError('El nombre no puede estar vacío');
      return;
    }
    
    if (!isNameUnique(newNodeName)) {
      setError('Ya existe una categoría con este nombre');
      return;
    }
    
    const categoriesCollection = getCategoriesCollection();
    if (!categoriesCollection) {
      setError('No hay cliente seleccionado');
      return;
    }
    
    setSaving(true);
    
    try {
      const newNode = {
        name: newNodeName,
        parentId: null,
        level: 0,
        order: categories.filter(c => c.level === 0).length,
        productCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await setDoc(doc(categoriesCollection), newNode);
      
      setNewNodeName('');
      setShowNewRootForm(false);
      setError(null);
    } catch (error) {
      console.error('Error adding category:', error);
      setError('Error al crear la categoría');
    } finally {
      setSaving(false);
    }
  };

  // Agregar subcategoría en Firestore
  const addChildCategory = async (parent: CategoryNode) => {
    const childName = window.prompt('Nombre de la subcategoría:');
    if (!childName || !childName.trim()) return;
    
    if (!isNameUnique(childName)) {
      window.alert('Ya existe una categoría con este nombre');
      return;
    }
    
    const categoriesCollection = getCategoriesCollection();
    if (!categoriesCollection) return;
    
    setSaving(true);
    
    try {
      const newChild = {
        name: childName,
        parentId: parent.id,
        level: parent.level + 1,
        order: parent.children.length,
        productCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await setDoc(doc(categoriesCollection), newChild);
      
      // Expandir el padre para mostrar la nueva subcategoría
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        newSet.add(parent.id);
        return newSet;
      });
    } catch (error) {
      console.error('Error adding subcategory:', error);
      window.alert('Error al crear la subcategoría');
    } finally {
      setSaving(false);
    }
  };

  // Editar categoría en Firestore
  const editCategory = async (node: CategoryNode) => {
    const newName = window.prompt('Nuevo nombre:', node.name);
    if (!newName || !newName.trim() || newName === node.name) return;
    
    if (!isNameUnique(newName, node.id)) {
      window.alert('Ya existe una categoría con este nombre');
      return;
    }
    
    const categoriesCollection = getCategoriesCollection();
    if (!categoriesCollection) return;
    
    setSaving(true);
    
    try {
      // Solo guardar los campos necesarios, no el objeto completo
      const updateData: any = {
        name: newName,
        updatedAt: Timestamp.now()
      };
      
      // Solo incluir parentId si existe
      if (node.parentId !== null) {
        updateData.parentId = node.parentId;
      }
      
      // Actualizar el documento
      await updateDoc(doc(categoriesCollection, node.id), updateData);
      
    } catch (error) {
      console.error('Error updating category:', error);
      window.alert('Error al actualizar la categoría: ' + (error as any).message);
    } finally {
      setSaving(false);
    }
  };

  // Eliminar categoría y sus subcategorías de Firestore
  const deleteCategory = async (node: CategoryNode) => {
    if (!window.confirm(`¿Eliminar "${node.name}" y todas sus subcategorías?`)) return;
    
    const categoriesCollection = getCategoriesCollection();
    if (!categoriesCollection) return;
    
    setSaving(true);
    
    try {
      // Obtener todos los IDs a eliminar (nodo y todos sus descendientes)
      const idsToDelete = new Set<string>([node.id]);
      
      const collectChildIds = (n: CategoryNode) => {
        n.children.forEach(child => {
          idsToDelete.add(child.id);
          collectChildIds(child);
        });
      };
      
      collectChildIds(node);
      
      // Eliminar en batch
      const batch = writeBatch(db);
      idsToDelete.forEach(id => {
        batch.delete(doc(categoriesCollection, id));
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting category:', error);
      window.alert('Error al eliminar la categoría');
    } finally {
      setSaving(false);
    }
  };

  // Manejar drag & drop para reordenar
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Detectar posición mientras se arrastra
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over) {
      setOverId(null);
      setDropIndicator(null);
      return;
    }
    
    // Evitar que un elemento se suelte sobre sí mismo
    if (active.id === over.id) {
      setDropIndicator(null);
      return;
    }
    
    setOverId(over.id as string);
    
    const draggedNode = findNodeById(categories, active.id as string);
    const targetNode = findNodeById(categories, over.id as string);
    
    if (!draggedNode || !targetNode) {
      setDropIndicator(null);
      return;
    }
    
    // Verificar que no se esté intentando mover un nodo dentro de sus propios descendientes
    const isDescendant = (parent: CategoryNode, childId: string): boolean => {
      if (parent.children) {
        for (const child of parent.children) {
          if (child.id === childId || isDescendant(child, childId)) {
            return true;
          }
        }
      }
      return false;
    };
    
    if (isDescendant(draggedNode, targetNode.id)) {
      setDropIndicator(null);
      return;
    }
    
    // Verificar límites de niveles
    const getMaxDepth = (node: CategoryNode): number => {
      if (!node.children || node.children.length === 0) return 0;
      return 1 + Math.max(...node.children.map(getMaxDepth));
    };
    
    const draggedDepth = getMaxDepth(draggedNode);
    
    let position: 'before' | 'after' | 'inside';
    let level: number;
    
    // Detectar si estamos arrastrando con teclas modificadoras
    const shiftKey = event.activatorEvent && (event.activatorEvent as any).shiftKey;
    const altKey = event.activatorEvent && (event.activatorEvent as any).altKey;
    
    // ALT = insertar como hermano (antes o después)
    // SHIFT = insertar al mismo nivel que el target
    // Sin teclas = insertar como hijo si es posible
    
    if (altKey || shiftKey) {
      // Forzar inserción como hermano
      position = 'after';
      level = targetNode.level;
    } else {
      // Intentar insertar como hijo si es posible
      const canBeChild = targetNode.level < 2 && 
                         targetNode.level + 1 + draggedDepth <= 2;
      
      if (canBeChild) {
        position = 'inside';
        level = targetNode.level + 1;
      } else {
        // Si no puede ser hijo, insertar como hermano
        position = 'after';
        level = targetNode.level;
      }
    }
    
    // Si estamos arrastrando sobre una categoría de nivel 0 y queremos moverla al nivel 0
    // detectamos esto cuando el dragged es de nivel > 0 y el target es de nivel 0 con shift/alt
    if (draggedNode.level > 0 && targetNode.level === 0 && (shiftKey || altKey)) {
      position = 'after';
      level = 0;
    }
    
    setDropIndicator({
      targetId: targetNode.id,
      position,
      level
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    
    // Guardar el indicador actual antes de limpiarlo
    const currentIndicator = dropIndicator;
    setDropIndicator(null);
    
    if (!over || active.id === over.id || !currentIndicator) return;
    
    const categoriesCollection = getCategoriesCollection();
    if (!categoriesCollection) return;
    
    setSaving(true);
    
    try {
      // Encuentra los nodos que se están moviendo
      const draggedNode = findNodeById(categories, active.id as string);
      const targetNode = findNodeById(categories, over.id as string);
      
      if (!draggedNode || !targetNode) return;
      
      // Verificar que no se esté intentando mover un nodo dentro de sus propios descendientes
      const isDescendant = (parent: CategoryNode, childId: string): boolean => {
        if (parent.children) {
          for (const child of parent.children) {
            if (child.id === childId || isDescendant(child, childId)) {
              return true;
            }
          }
        }
        return false;
      };
      
      if (isDescendant(draggedNode, targetNode.id)) {
        window.alert('No puedes mover una categoría dentro de sus propias subcategorías');
        return;
      }
      
      let newParentId: string | null;
      let newLevel: number;
      let newOrder: number;
      
      // Determinar el nuevo padre, nivel y orden basado en la posición del indicador
      if (currentIndicator.position === 'inside') {
        // Insertar dentro del target
        newParentId = targetNode.id;
        newLevel = targetNode.level + 1;
        newOrder = targetNode.children ? targetNode.children.length : 0;
      } else if (currentIndicator.position === 'before') {
        // Insertar antes del target
        newParentId = targetNode.parentId;
        newLevel = targetNode.level;
        newOrder = (targetNode.order || 0) - 0.5; // Insertamos con orden intermedio
      } else if (currentIndicator.position === 'after') {
        // Insertar después del target
        newParentId = targetNode.parentId;
        newLevel = targetNode.level;
        newOrder = (targetNode.order || 0) + 0.5; // Insertamos con orden intermedio
      } else {
        return;
      }
      
      // Verificar que no exceda el límite de 2 niveles
      if (newLevel > 2) {
        window.alert('No se pueden crear más de 2 niveles de subcategorías');
        return;
      }
      
      // Si el nodo arrastrado tiene hijos, verificar que todos quepan en el límite de niveles
      const getMaxDepth = (node: CategoryNode): number => {
        if (!node.children || node.children.length === 0) return 0;
        return 1 + Math.max(...node.children.map(getMaxDepth));
      };
      
      const draggedDepth = getMaxDepth(draggedNode);
      if (newLevel + draggedDepth > 2) {
        window.alert('Esta operación excedería el límite de 2 niveles de subcategorías');
        return;
      }
      
      // Actualizar en Firestore
      const batch = writeBatch(db);
      
      // Actualizar el parentId y orden del nodo arrastrado
      const draggedRef = doc(categoriesCollection, draggedNode.id);
      batch.update(draggedRef, {
        parentId: newParentId,
        level: newLevel,
        order: newOrder,
        updatedAt: Timestamp.now()
      });
      
      // Si el nodo tiene hijos, actualizar sus niveles recursivamente
      const updateChildrenLevels = (node: CategoryNode, baseLevel: number) => {
        if (node.children) {
          node.children.forEach(child => {
            const childRef = doc(categoriesCollection, child.id);
            batch.update(childRef, {
              level: baseLevel + 1,
              updatedAt: Timestamp.now()
            });
            updateChildrenLevels(child, baseLevel + 1);
          });
        }
      };
      
      updateChildrenLevels(draggedNode, newLevel);
      
      // Si el target es de nivel < 2, expandirlo para mostrar el nuevo hijo
      if (currentIndicator.position === 'inside') {
        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.add(targetNode.id);
          return newSet;
        });
      }
      
      await batch.commit();
      
      
    } catch (error) {
      console.error('Error al reordenar categorías:', error);
      window.alert('Error al mover la categoría');
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de atributos
  const openAttributesModal = (node: CategoryNode) => {
    setSelectedNodeForAttributes(node);
    setShowAttributesModal(true);
  };

  // Obtener atributos heredados del padre
  const getInheritedAttributes = (node: CategoryNode): CategoryAttribute[] => {
    if (!node.parentId) return [];
    const parent = findNodeById(categories, node.parentId);
    if (!parent) return [];
    return parent.attributes || [];
  };

  // Renderizar árbol recursivamente
  const renderTree = (nodes: CategoryNode[]) => {
    return nodes.map(node => (
      <div key={node.id}>
        <TreeNode
          node={node}
          expanded={expandedNodes.has(node.id)}
          onToggle={() => toggleNode(node.id)}
          onEdit={editCategory}
          onDelete={deleteCategory}
          onAddChild={addChildCategory}
          onOpenAttributes={openAttributesModal}
          searchTerm={searchTerm}
          isHighlighted={highlightedNodes.has(node.id)}
          isDragging={activeId === node.id}
          dropIndicator={dropIndicator}
          productCounts={productCounts}
          setSelectedCategoryForProducts={setSelectedCategoryForProducts}
          setShowProductsModal={setShowProductsModal}
        />
        {expandedNodes.has(node.id) && node.children && node.children.length > 0 && (
          <div>
            {renderTree(node.children)}
          </div>
        )}
      </div>
    ));
  };

  const flatNodes = useMemo(() => flattenTree(categories), [categories, flattenTree]);
  const sortableItems = flatNodes.map(node => node.id);

  // Mostrar mensaje si no hay cliente seleccionado
  if (!selectedClient || selectedClient.clientId === 'admin-mode') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Selecciona un cliente para gestionar sus categorías</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Gestión de Categorías</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            title="Expandir todo"
          >
            <Maximize2 className="w-4 h-4" />
            <span>Expandir</span>
          </button>
          <button
            onClick={collapseAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            title="Colapsar todo"
          >
            <Minimize2 className="w-4 h-4" />
            <span>Colapsar</span>
          </button>
        </div>
      </div>

      {/* Búsqueda y botones principales */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar categorías..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 ml-auto">
          <button
            onClick={() => setShowGlobalAttributesModal(true)}
            className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            title="Atributos Globales"
            style={{
              backgroundColor: 'rgba(147, 51, 234, 0.1)',
              color: 'rgb(147, 51, 234)',
              border: '1px solid rgba(147, 51, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.1)';
            }}
          >
            <Globe className="w-4 h-4" />
            <span>Atributos Globales</span>
            {globalAttributesCount > 0 && (
              <span 
                className="text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold"
                style={{
                  color: 'rgb(147, 51, 234)',
                  backgroundColor: 'transparent',
                  border: '1.5px solid rgb(147, 51, 234)'
                }}
              >
                {globalAttributesCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowRulesModal(true)}
            className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            title="Reglas de Catalogación"
            style={{
              backgroundColor: 'rgba(219, 39, 119, 0.1)',
              color: 'rgb(219, 39, 119)',
              border: '1px solid rgba(219, 39, 119, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(219, 39, 119, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(219, 39, 119, 0.1)';
            }}
          >
            <FileText className="w-4 h-4" />
            <span>Reglas</span>
            {rulesCount > 0 && (
              <span
                className="text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold"
                style={{
                  color: 'rgb(219, 39, 119)',
                  backgroundColor: 'transparent',
                  border: '1.5px solid rgb(219, 39, 119)'
                }}
              >
                {rulesCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowClassifierModal(true)}
            className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            title="Reglas de Clasificación (out-of-scope)"
            style={{
              backgroundColor: 'rgba(234, 88, 12, 0.1)',
              color: 'rgb(234, 88, 12)',
              border: '1px solid rgba(234, 88, 12, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(234, 88, 12, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(234, 88, 12, 0.1)';
            }}
          >
            <Filter className="w-4 h-4" />
            <span>Clasificación</span>
          </button>
          <button
            onClick={() => setShowNewRootForm(true)}
            className="px-4 py-2 border border-blue-500 text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            <Plus className="w-4 h-4" />
            Nueva Categoría
          </button>
        </div>
      </div>

      {/* Formulario nueva categoría raíz */}
      {showNewRootForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Nombre de la nueva categoría principal"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={newNodeName}
              onChange={(e) => {
                setNewNodeName(e.target.value);
                setError(null);
              }}
              onKeyPress={(e) => e.key === 'Enter' && addRootCategory()}
              autoFocus
              disabled={saving}
            />
            <button
              onClick={addRootCategory}
              className="btn btn-primary flex items-center gap-2"
              disabled={saving}
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
            <button
              onClick={() => {
                setShowNewRootForm(false);
                setNewNodeName('');
                setError(null);
              }}
              className="btn btn-default"
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {error && (
            <div className="mt-2 flex items-center text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </div>
          )}
        </div>
      )}

      {/* Estado de guardado */}
      {saving && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
          <Loader className="w-4 h-4 animate-spin text-blue-600 mr-2" />
          <span className="text-sm text-blue-700">Guardando cambios...</span>
        </div>
      )}

      {/* Árbol de categorías */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        {loading ? (
          <div className="text-center py-8">
            <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Cargando categorías...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No hay categorías creadas</p>
            <button
              onClick={() => setShowNewRootForm(true)}
              className="mt-3 text-blue-600 hover:text-blue-700"
            >
              Crear primera categoría
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {renderTree(categories)}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <div className="bg-white shadow-lg rounded p-2 opacity-80">
                  {findNodeById(categories, activeId)?.name}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Modal de Atributos */}
      {showAttributesModal && selectedNodeForAttributes && selectedClient && (
        <AttributesModal
          isOpen={showAttributesModal}
          onClose={() => {
            setShowAttributesModal(false);
            setSelectedNodeForAttributes(null);
          }}
          categoryId={selectedNodeForAttributes.id}
          categoryName={selectedNodeForAttributes.name}
          clientId={selectedClient.clientId}
          inheritedAttributes={getInheritedAttributes(selectedNodeForAttributes)}
        />
      )}

      {/* Modal de Atributos Globales */}
      {showGlobalAttributesModal && selectedClient && (
        <GlobalAttributesModal
          isOpen={showGlobalAttributesModal}
          onClose={() => setShowGlobalAttributesModal(false)}
          clientId={selectedClient.clientId}
        />
      )}

      {/* Modal de Reglas Generales */}
      {showRulesModal && selectedClient && (
        <GeneralRulesModal
          isOpen={showRulesModal}
          onClose={() => setShowRulesModal(false)}
          clientId={selectedClient.clientId}
          categories={getAllCategoriesFlat()}
        />
      )}

      {/* Modal de Reglas de Clasificación (out-of-scope) */}
      {showClassifierModal && selectedClient && (
        <ClassifierRulesModal
          isOpen={showClassifierModal}
          onClose={() => setShowClassifierModal(false)}
          clientId={selectedClient.clientId}
        />
      )}

      {/* Modal de Productos Filtrados */}
      {showProductsModal && currentTableName && (
        <FilteredProductsModal
          isOpen={showProductsModal}
          onClose={() => {
            setShowProductsModal(false);
            setSelectedCategoryForProducts('');
          }}
          tableName={currentTableName}
          filterType="category"
          filterValue={selectedCategoryForProducts}
          title={`Productos de ${selectedCategoryForProducts}`}
        />
      )}

    </div>
  );
};

export default Categorias;